import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ArrowLeft, Search, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Location } from '../../types';
import { BRANDS, BRAND_ICONS, normalizeBrandName, Brand } from '../../utils/brands';
import { calculateDistance, formatDistance } from '../../utils/distance';
import './BrandsListView.css';

interface BrandsListViewProps {
  allLocations?: Record<string, Location>;
  userLocation?: [number, number] | null;
  referenceLocation?: [number, number] | null;
  onBack: () => void;
  onBrandClick: (brand: string, locations: Array<Location & { distance?: number }>, referenceLocation: [number, number] | null) => void;
}

const ITEMS_PER_PAGE = 20; // Number of items to load per batch

const BrandsListView: React.FC<BrandsListViewProps> = ({
  allLocations = {},
  userLocation,
  referenceLocation,
  onBack,
  onBrandClick,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Use referenceLocation (from Home2) if available, otherwise fall back to userLocation
  const currentLocation = referenceLocation || userLocation || null;

  // Get all brands with their location counts and distances
  const allBrandsWithData = useMemo(() => {
    if (!allLocations) return [];

    const brandMap: Record<string, {
      brand: string;
      locations: Array<Location & { distance?: number }>;
      closestDistance?: number;
      locationCount: number;
    }> = {};

    BRANDS.forEach((brand) => {
      const locations: Array<Location & { distance?: number }> = [];
      const brandLower = normalizeBrandName(brand).toLowerCase();

      Object.entries(allLocations).forEach(([key, loc]) => {
        if (!loc || !loc.latitude || !loc.longitude) return;

        const locationWithName = {
          ...loc,
          location: loc.location || key,
          _id: loc._id || key,
        };

        const locationNameLower = (locationWithName.location || '').toLowerCase();
        let locationMatches = false;

        // Check if location name matches brand
        if (locationNameLower.includes(brandLower)) {
          locationMatches = true;
        }

        // Check shops within locations
        if (locationWithName.shops) {
          const shops = Array.isArray(locationWithName.shops)
            ? locationWithName.shops
            : Object.values(locationWithName.shops);

          shops.forEach((shop: any) => {
            if (!shop) return;

            const shopName = typeof shop === 'string'
              ? shop
              : (shop.name || shop.storeName || shop.title || shop.shopName || '');

            const normalizedShopName = normalizeBrandName(shopName as Brand).toLowerCase();
            if (normalizedShopName.includes(brandLower) || shopName.toLowerCase() === brandLower) {
              locationMatches = true;
            }
          });
        }

        if (locationMatches) {
          const existingIndex = locations.findIndex(l => l._id === locationWithName._id);
          if (existingIndex === -1) {
            let distance: number | undefined;
            if (currentLocation) {
              distance = calculateDistance(
                currentLocation[0],
                currentLocation[1],
                locationWithName.latitude,
                locationWithName.longitude
              );
            }
            locations.push({ ...locationWithName, distance });
          }
        }
      });

      // Sort by distance when currentLocation is available
      locations.sort((a, b) => {
        if (currentLocation) {
          if (a.distance === undefined && a.latitude && a.longitude) {
            a.distance = calculateDistance(
              currentLocation[0],
              currentLocation[1],
              a.latitude,
              a.longitude
            );
          }
          if (b.distance === undefined && b.latitude && b.longitude) {
            b.distance = calculateDistance(
              currentLocation[0],
              currentLocation[1],
              b.latitude,
              b.longitude
            );
          }
          if (a.distance !== undefined && b.distance !== undefined) {
            return a.distance - b.distance;
          }
          if (a.distance !== undefined) return -1;
          if (b.distance !== undefined) return 1;
        }
        return (a.location || '').localeCompare(b.location || '');
      });

      // Calculate closest distance
      const closestDistance = locations.length > 0 && locations[0].distance !== undefined
        ? locations[0].distance
        : undefined;

      brandMap[brand] = {
        brand,
        locations,
        closestDistance,
        locationCount: locations.length,
      };
    });

    return Object.values(brandMap);
  }, [allLocations, currentLocation]);

  // Filter brands based on search query
  const filteredBrands = useMemo(() => {
    if (!searchQuery.trim()) return allBrandsWithData;

    const query = searchQuery.toLowerCase();
    return allBrandsWithData.filter((brandData) => {
      const brandName = brandData.brand.toLowerCase();
      return brandName.includes(query);
    });
  }, [allBrandsWithData, searchQuery]);

  // Sort brands by closest distance when currentLocation is available, otherwise by name
  const sortedBrands = useMemo(() => {
    const sorted = [...filteredBrands];
    
    sorted.sort((a, b) => {
      // When using current location, prioritize by closest distance
      if (currentLocation) {
        if (a.closestDistance !== undefined && b.closestDistance !== undefined) {
          return a.closestDistance - b.closestDistance;
        }
        if (a.closestDistance !== undefined) return -1;
        if (b.closestDistance !== undefined) return 1;
      }
      // Otherwise sort by name
      return a.brand.localeCompare(b.brand);
    });

    return sorted;
  }, [filteredBrands, currentLocation]);

  // Get displayed items (paginated)
  const displayedItems = useMemo(() => {
    return sortedBrands.slice(0, displayedCount);
  }, [sortedBrands, displayedCount]);

  // Infinite scroll using Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && displayedCount < sortedBrands.length) {
          // Load more items
          setDisplayedCount((prev) => Math.min(prev + ITEMS_PER_PAGE, sortedBrands.length));
        }
      },
      {
        root: null,
        rootMargin: '100px', // Start loading before reaching the bottom
        threshold: 0.1,
      }
    );

    const target = observerTarget.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [displayedCount, sortedBrands.length]);

  // Reset displayed count when search query changes
  useEffect(() => {
    setDisplayedCount(ITEMS_PER_PAGE);
  }, [searchQuery]);

  const handleBrandClick = (brandData: typeof sortedBrands[0]) => {
    onBrandClick(brandData.brand, brandData.locations, currentLocation);
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="brands-list-view"
    >
      {/* Header */}
      <div className="brands-list-header">
        <button className="brands-list-back-button" onClick={onBack}>
          <ArrowLeft size={24} />
        </button>
        <div className="brands-list-title-section">
          <h1 className="brands-list-title">Brands</h1>
        </div>
        <div style={{ width: 40 }} /> {/* Spacer for centering */}
      </div>

      {/* Search Bar */}
      <div className="brands-list-search-container">
        <div className="brands-list-search-bar">
          <Search size={20} className="brands-list-search-icon" />
          <input
            type="text"
            placeholder="Search brands"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="brands-list-search-input"
          />
        </div>
      </div>

      {/* Brands List */}
      <div className="brands-list-content">
        {displayedItems.length === 0 ? (
          <div className="brands-list-empty">
            <p>No brands found</p>
          </div>
        ) : (
          <>
            {displayedItems.map((brandData, index) => {
              const BrandIcon = BRAND_ICONS[brandData.brand as keyof typeof BRAND_ICONS] || Search;
              const distance = brandData.closestDistance;

              return (
                <motion.div
                  key={brandData.brand}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.02, 0.3) }}
                  className="brands-list-item"
                  onClick={() => handleBrandClick(brandData)}
                >
                  <div className="brands-list-item-icon">
                    <BrandIcon size={24} />
                  </div>
                  <div className="brands-list-item-content">
                    <h3 className="brands-list-item-name">{brandData.brand}</h3>
                    <p className="brands-list-item-subtitle">
                      {brandData.locationCount} {brandData.locationCount === 1 ? 'location' : 'locations'}
                    </p>
                  </div>
                  <div className="brands-list-item-right">
                    {distance !== undefined && (
                      <span className="brands-list-item-distance">
                        {formatDistance(distance)}
                      </span>
                    )}
                    <ChevronRight size={20} className="brands-list-item-arrow" />
                  </div>
                </motion.div>
              );
            })}
            
            {/* Loading trigger element for infinite scroll */}
            {displayedCount < sortedBrands.length && (
              <div ref={observerTarget} className="brands-list-loading-trigger">
                <div className="brands-list-loading-spinner" />
              </div>
            )}
            
            {/* End of list indicator */}
            {displayedCount >= sortedBrands.length && sortedBrands.length > 0 && (
              <div className="brands-list-end">
                <p>Showing all {sortedBrands.length} brands</p>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

export default BrandsListView;

